<template>
	<v-row class="full-height receiving-list-page">
		<v-col cols="12" class="flex-none pb-0">
			<v-card class="receiving-list-toolbar-card" flat>
				<v-card-text class="pa-3">
					<v-row align="center" dense>
						<!-- Search -->
						<v-col cols="12" sm="6" md="4" lg="3">
							<v-text-field
								v-model="searchForm.searchTerm"
								placeholder="PO Number, Style Code, Vendor Name"
								prepend-inner-icon="mdi-magnify"
								outlined
								dense
								hide-details
								clearable
								class="receiving-list-search"
								@input="onSearchInput"
								@click:clear="onSearchClear"
							></v-text-field>
						</v-col>

						<!-- Filter -->
						<v-col cols="6" sm="3" md="2">
							<v-select
								v-model="searchForm.selectedFilters"
								:items="filterOptions"
								label="Filters"
								prepend-icon="mdi-filter"
								multiple
								clearable
								outlined
								dense
								hide-details
								@change="onFilterChange"
								@click:clear="onFilterClear"
							></v-select>
						</v-col>

						<!-- Spacer -->
						<v-spacer class="d-none d-md-flex"></v-spacer>

						<!-- Quick Receive Button -->
						<v-col cols="auto">
							<v-btn
								depressed
								rounded
								color="success"
								@click="navigateToQuickReceive()"
								:disabled="!canReceive"
							>
								Quick Receive
							</v-btn>
						</v-col>
					</v-row>
				</v-card-text>
			</v-card>
		</v-col>

		<v-col cols="12" class="flex-auto pt-0 receiving-list-table-container">
			<div v-if="loadingFromQP" class="loading-overlay">
				<div class="loading-dot">&nbsp;</div>
				<div class="loading-dot">&nbsp;</div>
				<div class="loading-dot">&nbsp;</div>
				<div class="loading-dot">&nbsp;</div>
				<div class="loading-dot">&nbsp;</div>
			</div>

			<v-data-table
				:headers="headers"
				:items="purchaseOrders"
				:loading="loading"
				:options.sync="tableOptions"
				:server-items-length="totalCount"
				:footer-props="footerProps"
				:mobile-breakpoint="0"
				class="receiving-list-data-table"
				@click:row="navigateToReceivingView"
				must-sort
				fixed-header
			>
				<!-- Order Date Column -->
				<template v-slot:item.orderDate="{ item }">
					{{ formatDate(item.orderDate) }}
				</template>

				<!-- Order Status Column -->
				<template v-slot:item.orderStatus="{ item }">
					<StatusBadge :value="item.orderStatus" />
				</template>

				<!-- Dropship Column -->
				<template v-slot:item.dropShip="{ item }">
					<v-icon v-if="item.dropShip" small color="success">mdi-check</v-icon>
				</template>

				<!-- Loading slot -->
				<template v-slot:loading>
					<v-skeleton-loader type="table-row@10"></v-skeleton-loader>
				</template>

				<!-- No data slot -->
				<template v-slot:no-data>
					<div class="text-center pa-4">
						<v-icon large color="grey lighten-1">mdi-package-variant</v-icon>
						<p class="mt-2 grey--text">No receiving entries found</p>
					</div>
				</template>
			</v-data-table>
		</v-col>
	</v-row>
</template>

<script>
import MixinDateFormatting from "@/mixins/DateFormatting";
import queries from "@/gql/queries";
import StatusBadge from "@/components/StatusBadge.vue";
import { WellKnownRoles, hasPermission, SystemPermissions } from "@/models/User";

export default {
	name: "ReceivingList",
	components: {
		StatusBadge,
	},
	mixins: [MixinDateFormatting],
	data() {
		return {
			loading: true,
			searchForm: {
				searchTerm: "",
				selectedFilters: [],
			},
			purchaseOrders: [],
			totalCount: 0,
			searchTimeout: null,
			loadingFromQP: false,
			tableOptions: {
				page: 1,
				itemsPerPage: 15,
				sortBy: ["orderDate"],
				sortDesc: [true],
			},
			footerProps: {
				"items-per-page-options": [10, 15, 25, 50],
				"show-first-last-page": true,
			},
			filterOptions: [
				{ text: "Open Orders", value: "onlyOpenOrder" },
				{ text: "Manual Orders", value: "onlyManualOrder" },
				{ text: "Dropship Orders", value: "onlyDropShipOrder" },
				{ text: "Cancelled Orders", value: "onlyCancelledOrder" },
			],
			headers: [
				{
					text: "PO #",
					value: "orderNumber",
					sortable: true,
					width: "10%",
				},
				{
					text: "PO Order Date",
					value: "orderDate",
					sortable: true,
					width: "10%",
				},
				{
					text: "Style",
					value: "styleCode",
					sortable: true,
					width: "10%",
				},
				{
					text: "Vendor",
					value: "vendor",
					sortable: true,
					width: "19%",
				},
				{
					text: "PO Status",
					value: "orderStatus",
					sortable: true,
					width: "14%",
				},
				{
					text: "Order Type",
					value: "orderType",
					sortable: true,
					width: "8%",
					align: "center",
				},
				{
					text: "Ordered",
					value: "ordered",
					sortable: true,
					width: "8%",
					align: "center",
				},
				{
					text: "Received",
					value: "received",
					sortable: true,
					width: "8%",
					align: "center",
				},
				{
					text: "Dropship",
					value: "dropShip",
					sortable: true,
					width: "6%",
					align: "center",
				},
			],
		};
	},
	apollo: {
		purchaseOrdersQuery: {
			query: () => queries.SEARCH_PURCHASE_ORDER_ITEMS,
			variables() {
				return this.buildQueryVariables();
			},
			update(data) {
				this.loading = false;
				this.totalCount = data.response?.totalCount || 0;
				this.purchaseOrders = data.response?.items || [];
				return data.response;
			},
			error(error) {
				console.error("Error fetching receiving entries:", error);
				this.loading = false;
			},
		},
	},
	watch: {
		tableOptions: {
			handler() {
				this.fetchData();
			},
			deep: true,
		},
		navigationDirection: {
			handler(newDirection) {
				if (newDirection) {
					// Back/forward navigation - restore saved state
					const savedState = this.$store.getters["globalState/receivingListState"];
					if (savedState) {
						// Restore page from offset
						const page = Math.floor(savedState.offset / this.tableOptions.itemsPerPage) + 1;
						this.tableOptions.page = page;
						this.searchForm.selectedFilters = [...savedState.selectedFilters];
						this.searchForm.searchTerm = savedState.searchTerm;
					}
				} else {
					// Direct navigation (not back/forward) - clear saved state if no query params
					const hasQueryParams = this.$route.query.purchaseOrderNumber;
					if (!hasQueryParams) {
						this.$store.dispatch("globalState/clearReceivingListState");
					}
				}
			},
			immediate: true,
		},
		"$route.query.purchaseOrderNumber": {
			handler(newPurchaseOrderNumber) {
				if (newPurchaseOrderNumber) {
					this.loadOrderFromQueryParam();
				}
			},
			immediate: true,
		},
	},
	computed: {
		canReceive() {
			const user = this.$store.getters.userInfo;
			return (
				hasPermission(user, SystemPermissions.Receiving_ReceivingEntry_Receive_UnReceive) ||
				user.isAdmin
			);
		},
		canViewEntry() {
			const user = this.$store.getters.userInfo;
			return (
				hasPermission(user, SystemPermissions.Receiving_ReceivingEntry_Search_View) || user.isAdmin
			);
		},
		navigationDirection() {
			return this.$store.getters["globalState/navigationDirection"];
		},
	},
	methods: {
		formatDate(date) {
			if (!date) return "";
			date = date.substring(0, 10);
			const [year, month, day] = date.split("-");
			return `${month}/${day}/${year}`;
		},

		navigateToQuickReceive() {
			this.$router.push("/receiving/receiving-entry/quick-receive");
		},

		navigateToReceivingView(row) {
			if (!this.canViewEntry) {
				return;
			}
			this.$router.push(`/receiving/receiving-entry/${row.purchaseOrderId}`);
		},

		onSearchInput() {
			// Debounced search
			if (this.searchTimeout) {
				clearTimeout(this.searchTimeout);
			}
			this.searchTimeout = setTimeout(() => {
				this.tableOptions.page = 1;
				this.fetchData();
			}, 400);
		},

		onSearchClear() {
			if (this.searchTimeout) {
				clearTimeout(this.searchTimeout);
			}
			this.tableOptions.page = 1;
			this.fetchData();
		},

		onFilterChange() {
			this.tableOptions.page = 1;
			this.fetchData();
		},

		onFilterClear() {
			this.$nextTick(() => {
				this.tableOptions.page = 1;
				this.fetchData();
			});
		},

		// Map table column values to GraphQL sort field names
		getSortFieldName(columnValue) {
			const sortFieldMap = {
				orderNumber: "purchaseOrderNumber",
				orderDate: "purchaseOrderDate",
				styleCode: "styleCode",
				vendor: "vendor",
				orderStatus: "orderStatus",
				orderType: "orderType",
				ordered: "ordered",
				received: "received",
				dropShip: "dropShip",
			};
			return sortFieldMap[columnValue] || "purchaseOrderDate";
		},

		buildQueryVariables() {
			const { page, itemsPerPage, sortBy, sortDesc } = this.tableOptions;
			const keyword = (this.searchForm.searchTerm || "").trim();

			// Keyword search is routed through the `searchTerm` argument, where the
			// resolver applies FTS on StyleCode/Vendor and LIKE on PurchaseOrderNumber.
			// Only pass 3+ char keywords; shorter terms short-circuit server-side.
			// HotChocolate's `where.contains` path is non-sargable (CHARINDEX + empty-LIKE),
			// so we avoid it for keyword search.
			const where = { and: [] };
			const searchTermArg = keyword.length >= 3 ? keyword : "";

			// Add filter conditions
			if (this.searchForm.selectedFilters.includes("onlyOpenOrder")) {
				where.and.push({ isCancelled: { eq: false } });
			}
			if (this.searchForm.selectedFilters.includes("onlyManualOrder")) {
				where.and.push({
					or: [
						{ purchaseOrderNumber: { startsWith: "7" } },
						{ purchaseOrderNumber: { startsWith: "9" } },
					],
				});
			}
			if (this.searchForm.selectedFilters.includes("onlyDropShipOrder")) {
				where.and.push({ dropShip: { eq: true } });
			}
			if (this.searchForm.selectedFilters.includes("onlyCancelledOrder")) {
				where.and.push({ isCancelled: { eq: true } });
			}

			// Add sales rep filter if user is a rep
			const user = this.$store.getters.userInfo;
			if (this.$userService.hasRole(user, WellKnownRoles.REP) && user.claims["sales_rep_id"]) {
				where.and.push({ salesRepId: { eq: parseInt(user.claims["sales_rep_id"]) } });
			}

			// Build orderStatus filter for Open Orders
			const orderStatus = [];
			if (this.searchForm.selectedFilters.includes("onlyOpenOrder")) {
				orderStatus.push("OnOrder", "PartiallyReceived");
			}

			// Build dynamic sort order
			const sortColumn = sortBy[0] || "orderDate";
			const sortDirection = sortDesc[0] ? "DESC" : "ASC";
			const sortFieldName = this.getSortFieldName(sortColumn);
			const order = [{ [sortFieldName]: sortDirection }];

			return {
				offset: (page - 1) * itemsPerPage,
				take: itemsPerPage,
				order,
				searchTerm: searchTermArg,
				orderStatus,
				where: where.and.length > 0 ? where : null,
			};
		},

		async fetchData() {
			this.loading = true;
			if (this.$apollo.queries.purchaseOrdersQuery) {
				await this.$apollo.queries.purchaseOrdersQuery.refetch();
			}
		},

		async searchWithRetry(maxRetries) {
			let retries = 0;
			while (retries < maxRetries) {
				await this.$apollo.queries.purchaseOrdersQuery?.refetch();
				await this.$nextTick();

				let purchaseOrderNumber = this.$route.query.purchaseOrderNumber;
				if (purchaseOrderNumber && this.purchaseOrders && this.purchaseOrders.length > 0) {
					// Search term could match orderNumber, styleCode, or vendor - check all
					const order = this.purchaseOrders.find(
						(q) => q.orderNumber === purchaseOrderNumber || q.styleCode === purchaseOrderNumber
					);
					// If only one result found, navigate to it directly
					const targetOrder = order || (this.purchaseOrders.length === 1 ? this.purchaseOrders[0] : null);
					if (targetOrder) {
						this.$router.push(`/receiving/receiving-entry/${targetOrder.purchaseOrderId}`);
						return;
					}
				}

				retries++;
				console.log(`Retry attempt ${retries}`);
				await new Promise((resolve) => setTimeout(resolve, 250));
			}

			console.log("Max retries reached. Order not found.");
		},

		async loadOrderFromQueryParam() {
			let purchaseOrderNumber = this.$route.query.purchaseOrderNumber;
			let filters = this.$route.query.filters;

			if (purchaseOrderNumber) {
				this.searchForm.searchTerm = purchaseOrderNumber;

				// Apply filters from query param (comma-separated list)
				if (filters) {
					this.searchForm.selectedFilters = filters.split(",");
				}

				this.loadingFromQP = true;
				await this.searchWithRetry(10);
				this.loadingFromQP = false;
			}
		},
	},
	beforeRouteLeave(to, from, next) {
		const offset = (this.tableOptions.page - 1) * this.tableOptions.itemsPerPage;
		this.$store.dispatch("globalState/saveReceivingListState", {
			searchTerm: this.searchForm.searchTerm,
			offset,
			selectedFilters: [...this.searchForm.selectedFilters],
			scrollPosition: window.scrollY,
		});
		next();
	},
	beforeDestroy() {
		if (this.searchTimeout) {
			clearTimeout(this.searchTimeout);
		}
	},
};
</script>

<style lang="scss" scoped>
.receiving-list-page {
	// Use calc to fit within viewport: 100vh - header (64px) - container padding (24px)
	height: calc(100vh - 88px);
	display: flex;
	flex-direction: column;
	overflow: hidden;
}

.receiving-list-table-container {
	display: flex;
	flex-direction: column;
	flex: 1;
	min-height: 0; // Critical for flex children to respect overflow
	overflow: hidden;
	position: relative;
}

.receiving-list-toolbar-card {
	border-radius: 4px 4px 0 0 !important;
	border-bottom: 1px solid $color-gray;
	flex-shrink: 0; // Don't shrink the toolbar
}

.receiving-list-search {
	::v-deep .v-input__slot {
		background-color: $color-gray-light-2 !important;
	}
}

.loading-overlay {
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background: rgba(255, 255, 255, 0.7);
	display: flex;
	justify-content: center;
	align-items: center;
	z-index: 10;
}

.loading-dot {
	width: 8px;
	height: 8px;
	margin: 0 4px;
	border-radius: 50%;
	background-color: #1976d2;
	animation: bounce 0.5s ease-in-out infinite;

	&:nth-child(2) {
		animation-delay: 0.1s;
	}
	&:nth-child(3) {
		animation-delay: 0.2s;
	}
	&:nth-child(4) {
		animation-delay: 0.3s;
	}
	&:nth-child(5) {
		animation-delay: 0.4s;
	}
}

@keyframes bounce {
	0%,
	100% {
		transform: translateY(0);
	}
	50% {
		transform: translateY(-10px);
	}
}

.receiving-list-data-table {
	border-radius: 0 0 4px 4px !important;
	display: flex;
	flex-direction: column;
	flex: 1;
	min-height: 0; // Critical for flex children to respect overflow
	overflow: hidden;

	// Table wrapper should scroll
	::v-deep .v-data-table__wrapper {
		flex: 1;
		overflow-y: auto;
		overflow-x: auto;

		table {
			min-width: 1100px;
		}
	}

	// Keep footer fixed at bottom
	::v-deep .v-data-footer {
		flex-shrink: 0;
	}

	// Make rows clickable
	::v-deep tbody tr {
		cursor: pointer;
		transition: background-color 0.2s;

		&:hover {
			background-color: $color-gray-light !important;
		}
	}

	// Match existing header styling
	::v-deep > .v-data-table__wrapper > table > thead > tr > th {
		font-size: 1.4rem !important;
		font-weight: 500 !important;
		color: #000 !important;
		text-transform: uppercase;
		background-color: $color-gray-light-2 !important;
		white-space: nowrap;
	}

	// Match existing row height and font size
	::v-deep > .v-data-table__wrapper > table > tbody > tr > td {
		height: 60px !important;
		font-size: 1.6rem !important;
	}

	// Footer styling
	::v-deep .v-data-footer {
		font-size: 1.25rem !important;
	}

	::v-deep .v-data-footer__select .v-select__selections .v-select__selection--comma {
		font-size: 1.15rem !important;
	}
}
</style>
